import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useProfile } from '@/hooks/use-profile';
import { getRedirectPath } from '@/utils/authFlow';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Upload, Camera, ArrowRight } from 'lucide-react';
import WelcomeShareCard from '@/components/onboarding/WelcomeShareCard';
import { RoadmapSection } from '@/components/landing/RoadmapSection';
import { motion, AnimatePresence } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import { LoadingFallback } from '@/components/common/LoadingFallback';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { validateUsername } from '@/utils/profileValidation';

const CreateUsername = () => {
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [showWelcomeCard, setShowWelcomeCard] = useState(false);
  const [showRoadmap, setShowRoadmap] = useState(false);
  const [hasSeenOnboarding, setHasSeenOnboarding] = useState(false);
  const [showClaimDialog, setShowClaimDialog] = useState(false);
  const [claimMessage, setClaimMessage] = useState('');
  const [claimLoading, setClaimLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, profile: authProfile, isLoading: authLoading, refreshProfile } = useAuth();
  const { profile, isLoading: profileLoading, refetch } = useProfile(user?.id);
  const queryClient = useQueryClient();

  console.log('CreateUsername - State:', {
    user,
    profile,
    authLoading,
    profileLoading,
    hasCompletedOnboarding: profile?.has_completed_onboarding,
    timestamp: new Date().toISOString()
  });

  useEffect(() => {
    if (!authLoading && !user) {
      console.log('CreateUsername - No user found, redirecting to home');
      navigate('/home', { replace: true });
      return;
    }

    // Only check profile and redirect if we have a user and profile loaded
    // BUT: Don't redirect if showing welcome card or roadmap (user just completed onboarding)
    if (!authLoading && !profileLoading && user && profile && !showWelcomeCard && !showRoadmap) {
      console.log('CreateUsername - Checking profile status', {
        hasCompletedOnboarding: profile?.has_completed_onboarding,
        isUsernameCustomized: profile?.is_username_customized
      });

      // Redirect if user has already completed onboarding (only if not in welcome flow)
      if (profile.has_completed_onboarding && hasSeenOnboarding) {
        console.log('CreateUsername - User has completed onboarding, redirecting to bulletins');
        navigate('/bulletins', { replace: true });
        return;
      }

      // Check for customized username
      if (profile.is_username_customized && hasSeenOnboarding) {
        const redirectPath = getRedirectPath(profile);
        if (redirectPath !== '/create-username') {
          console.log('CreateUsername - Username is customized, redirecting to:', redirectPath);
          navigate(redirectPath, { replace: true });
          return;
        }
      }
    }
  }, [profile, navigate, user, authLoading, profileLoading, showWelcomeCard, showRoadmap, hasSeenOnboarding]);

  useEffect(() => {
    if (user?.id) {
      const hasSeenBefore = localStorage.getItem(`hasSeenOnboarding_${user.id}`);
      if (hasSeenBefore === 'true') {
        setHasSeenOnboarding(true);
      }
    }
  }, [user?.id]);

  if (authLoading || profileLoading) {
    return <LoadingFallback />;
  }

  if (!user) {
    return null;
  }

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>, type: 'avatar' | 'banner') => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please upload an image file",
        variant: "destructive"
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      if (type === 'avatar') {
        setAvatarFile(file);
        setAvatarPreview(reader.result as string);
      } else {
        setBannerFile(file);
        setBannerPreview(reader.result as string);
      }
    };
    reader.readAsDataURL(file);
  };

  const uploadImage = async (file: File, type: 'avatar' | 'banner') => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}-${type}.${fileExt}`;
    const { data, error } = await supabase.storage
      .from('profile-images')
      .upload(fileName, file, { upsert: true });

    if (error) throw error;

    const { data: { publicUrl } } = supabase.storage
      .from('profile-images')
      .getPublicUrl(fileName);

    return publicUrl;
  };

  const handleUsernameClaimSubmit = async () => {
    if (!user?.email || !username) return;
    
    setClaimLoading(true);
    try {
      const { error } = await supabase
        .from('username_claim_requests')
        .insert([{
          username,
          email: user.email,
          message: claimMessage || null
        }]);

      if (error) throw error;

      toast({
        title: "Claim request submitted",
        description: "We'll review your request and get back to you soon.",
      });
      setShowClaimDialog(false);
    } catch (error: any) {
      console.error('Error submitting claim:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to submit claim request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setClaimLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setValidationError('');

    try {
      // Validate username
      const validation = validateUsername(username);
      if (!validation.isValid) {
        setValidationError(validation.error || 'Invalid username');
        setIsLoading(false);
        return;
      }

      // Check if username already exists directly - no whitelisting check
      const { data: existingUser, error: checkError } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', username)
        .maybeSingle();

      if (checkError) throw checkError;
      if (existingUser) throw new Error('Username already taken');

      let profileImageUrl = null;
      let bannerImageUrl = null;

      if (avatarFile) {
        profileImageUrl = await uploadImage(avatarFile, 'avatar');
      }
      if (bannerFile) {
        bannerImageUrl = await uploadImage(bannerFile, 'banner');
      }

      console.log('[CreateUsername] Attempting profile update', {
        user_id: user?.id,
        username,
        display_name: displayName,
        has_images: { avatar: !!profileImageUrl, banner: !!bannerImageUrl }
      });

      const { data: updateData, error: updateError } = await supabase
        .from('profiles')
        .update({ 
          username,
          display_name: displayName,
          is_username_customized: true,
          has_completed_onboarding: true, // Set the new flag
          profile_image_url: profileImageUrl,
          banner_image_url: bannerImageUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', user?.id)
        .select();

      if (updateError) {
        console.error('[CreateUsername] Profile update FAILED:', updateError);
        throw updateError;
      }

      console.log('[CreateUsername] Profile update SUCCESS:', updateData);

      // CRITICAL: Clear profile cache to ensure fresh data on next fetch
      console.log('[CreateUsername] Clearing profile cache...');
      const { clearCachedProfile } = await import('@/utils/profile-cache');
      clearCachedProfile(user.id);

      // Force a refetch of the profile data in React Query
      await queryClient.invalidateQueries({ queryKey: ['profile', user?.id] });
      
      // CRITICAL: Refresh the AuthContext profile state so it has the updated data
      console.log('[CreateUsername] Refreshing AuthContext profile...');
      await refreshProfile();
      console.log('[CreateUsername] AuthContext profile refreshed');
      
      // Now refetch the useProfile hook data to sync it with AuthContext
      const refetchResult = await refetch();
      console.log('[CreateUsername] useProfile refetch result:', {
        hasData: !!refetchResult.data,
        has_completed_onboarding: refetchResult.data?.has_completed_onboarding,
        username: refetchResult.data?.username
      });

      toast({
        title: "Profile created!",
        description: "Welcome to FOMO.",
      });
      
      setShowWelcomeCard(true);
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen pt-20 flex items-center justify-center bg-gradient-to-br from-primary/20 via-background to-accent/10 p-4">
        <div className="relative w-full max-w-md space-y-8 bg-black/40 p-8 rounded-xl backdrop-blur-sm border border-white/10">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10 rounded-xl -z-10" />
          
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <h1 className="text-4xl sm:text-6xl font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent animate-fade-in">
                Create profile
              </h1>
            </div>
            
            {profile?.registration_number !== undefined && (
              <p className="text-2xl sm:text-3xl text-primary font-bold animate-fade-in tracking-tight">
                Welcome #{profile.registration_number}
              </p>
            )}
            
            <p className="text-base sm:text-lg text-gray-400 max-w-2xl animate-fade-in opacity-80">
              Make your mark on FOMO
            </p>
          </div>

          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            <div className="space-y-6">
              <div className="flex flex-col items-center gap-6">
                <div className="relative group">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageSelect(e, 'avatar')}
                    className="hidden"
                    id="avatar-upload"
                  />
                  <label htmlFor="avatar-upload" className="cursor-pointer">
                    <Avatar className="w-24 h-24 ring-2 ring-primary/20 group-hover:ring-primary/40 transition-all">
                      {avatarPreview ? (
                        <AvatarImage src={avatarPreview} className="object-cover" />
                      ) : (
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          F
                        </AvatarFallback>
                      )}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-full flex flex-col items-center justify-center gap-1">
                        <Camera className="w-6 h-6 text-white" />
                        <span className="text-xs text-white font-medium">Upload Avatar</span>
                      </div>
                    </Avatar>
                  </label>
                </div>

                <div className="relative w-full h-32 group">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageSelect(e, 'banner')}
                    className="hidden"
                    id="banner-upload"
                  />
                  <label htmlFor="banner-upload" className="cursor-pointer">
                    <div className="w-full h-32 rounded-xl bg-black/40 overflow-hidden ring-2 ring-primary/20 group-hover:ring-primary/40 transition-all">
                      {bannerPreview ? (
                        <img src={bannerPreview} alt="Banner preview" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                          <Upload className="w-6 h-6 text-gray-400" />
                          <span className="text-sm text-gray-400 font-medium">Upload Banner</span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Camera className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="displayName" className="text-white">Display Name</Label>
                <Input
                  id="displayName"
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Enter your name"
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                  required
                  maxLength={50}
                />
                <p className="text-sm text-gray-400">
                  This is how other users will know you
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="username" className="text-white">Username</Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Choose your username"
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                  required
                  minLength={5}
                  maxLength={20}
                  pattern="^[a-zA-Z0-9_]+$"
                />
                <p className="text-sm text-gray-400">
                  Username must be between 5-20 characters and can only contain letters, numbers, and underscores
                </p>
                {validationError && (
                  <p className="text-sm text-red-500 mt-1">
                    {validationError}
                  </p>
                )}
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? "Creating profile..." : "Continue"}
            </Button>
          </form>
        </div>
      </div>

      <Dialog open={showClaimDialog} onOpenChange={setShowClaimDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Request Username</DialogTitle>
            <DialogDescription>
              This username is reserved. Submit a request to claim it or email us at team@dontfomo.app
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="username">Username</Label>
              <Input id="username" value={username} disabled />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" value={user?.email || ''} disabled />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="message">Message (Optional)</Label>
              <Textarea
                id="message"
                placeholder="Tell us why you'd like this username..."
                value={claimMessage}
                onChange={(e) => setClaimMessage(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClaimDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUsernameClaimSubmit} disabled={claimLoading}>
              {claimLoading ? "Submitting..." : "Submit Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AnimatePresence>
        {showWelcomeCard && profile && !hasSeenOnboarding && (
          <WelcomeShareCard 
            profile={profile} 
            onClose={() => {
              setShowWelcomeCard(false);
              setShowRoadmap(true);
            }}
          />
        )}

        {showRoadmap && !hasSeenOnboarding && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={(e) => {
              // Allow clicking outside to continue
              if (e.target === e.currentTarget) {
                setShowRoadmap(false);
                setHasSeenOnboarding(true);
                localStorage.setItem(`hasSeenOnboarding_${user?.id}`, 'true');
                navigate('/bulletins', { replace: true });
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="w-full max-w-2xl bg-gradient-to-b from-gray-900 to-black rounded-[24px] overflow-hidden shadow-2xl p-8"
            >
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-white">Welcome to FOMO 🎉</h2>
                <p className="text-gray-400">
                  A new social media ecosystem designed to revolutionize how creators experience and engage with their audience.
                </p>
                
                <RoadmapSection isMobile={true} onNext={() => {
                  setShowRoadmap(false);
                  setHasSeenOnboarding(true);
                  localStorage.setItem(`hasSeenOnboarding_${user?.id}`, 'true');
                  navigate('/bulletins', { replace: true });
                }} />
                
                {/* Large prominent button */}
                <Button 
                  onClick={() => {
                    setShowRoadmap(false);
                    setHasSeenOnboarding(true);
                    localStorage.setItem(`hasSeenOnboarding_${user?.id}`, 'true');
                    navigate('/bulletins', { replace: true });
                  }}
                  className="w-full bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-700 text-white font-bold py-6 text-lg shadow-lg"
                  size="lg"
                >
                  Get Started with FOMO <ArrowRight className="ml-2" />
                </Button>
                
                <p className="text-xs text-gray-500 text-center">
                  Click anywhere outside to continue
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CreateUsername;
